import { useTranslation } from "react-i18next";
import GuestTile from "../../GuestTile/GuestTile";
import styles from "../Drawer.module.scss";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";
import groupDishes from "../../../helpers/groupDishes";
import { toppingsTotalGrosze } from "@/helpers/cartPricing";
import { useCallback, useEffect, useRef } from "react";

function PayForYourItems() {
  const { remainingGuests = [], setVenuesData } = useVenuesDataStore();
  const { i18n } = useTranslation();

  const checkedMapRef = useRef<Map<string, number>>(new Map());
  const dishIdsMapRef = useRef<Map<string, string[]>>(new Map());

  useEffect(() => {
    setVenuesData({
      selectedAmount: 0,
      splitMethod: "items",
      selectedDishIds: [],
    });
    checkedMapRef.current.clear();
    dishIdsMapRef.current.clear();
  }, [setVenuesData]);

  const getTranslatedName = (
    translations: { languageCode: string; name: string }[],
  ) => {
    const lang = i18n.language;
    return (
      translations?.find((t) => t.languageCode === lang)?.name ??
      translations?.[0]?.name ??
      ""
    );
  };

  const mapGuestDishes = (dishes: (typeof remainingGuests)[0]["dishes"]) =>
    groupDishes(dishes).map((dish) => {
      const unitPrice = dish.size?.price ?? dish.price;
      const dishTotal =
        (unitPrice * dish.count +
          toppingsTotalGrosze(dish.toppings) * dish.count) /
        100;
      return {
        count: dish.count,
        description: getTranslatedName(dish.translations),
        basePrice:
          dish.count > 1 ? `${(unitPrice / 100).toFixed(2)} zł` : undefined,
        price: `${dishTotal.toFixed(2)} zł`,
        numericPrice: dishTotal,
        ids: dish.ids as string[],
        toppings: dish.toppings?.map(
          (t: {
            translations: { languageCode: string; name: string }[];
            price: number;
            count: number;
          }) => ({
            name: getTranslatedName(t.translations),
            price: `${(t.price / 100).toFixed(2)} zł`,
          }),
        ),
      };
    });

  const recalculateTotal = useCallback(() => {
    let total = 0;
    for (const price of checkedMapRef.current.values()) {
      total += price;
    }
    const allIds: string[] = [];
    for (const ids of dishIdsMapRef.current.values()) {
      allIds.push(...ids);
    }
    setVenuesData({ selectedAmount: total, selectedDishIds: allIds });
  }, [setVenuesData]);

  const handleItemChecked = useCallback(
    (
      guestNumber: number,
      itemIndex: number,
      numericPrice: number,
      ids: string[],
      checked: boolean,
    ) => {
      const key = `${guestNumber}-${itemIndex}`;
      if (checked) {
        checkedMapRef.current.set(key, numericPrice);
        dishIdsMapRef.current.set(key, ids);
      } else {
        checkedMapRef.current.delete(key);
        dishIdsMapRef.current.delete(key);
      }
      recalculateTotal();
    },
    [recalculateTotal],
  );

  return (
    <div className={styles.payForYourItemsContent}>
      {remainingGuests.map((guest) => {
        const mappedDishes = mapGuestDishes(guest.dishes);
        return (
          <GuestTile
            key={guest.guestNumber}
            itemsToPay={mappedDishes}
            guestNumber={guest.guestNumber}
            onItemCheckedChange={(itemIndex, checked) =>
              handleItemChecked(
                guest.guestNumber,
                itemIndex,
                mappedDishes[itemIndex]?.numericPrice ?? 0,
                mappedDishes[itemIndex]?.ids ?? [],
                checked,
              )
            }
          />
        );
      })}
    </div>
  );
}

export default PayForYourItems;
